import { useCallback, useState } from 'react'
import { revalidateLogic, useForm } from '@tanstack/react-form'
import { FormDialog } from './form-dialog'
import type { FormDialogOptions } from '@/contexts/form-dialog-context'
import type { AnyReactFormApi } from '@/types/form'
import { FormDialogContext } from '@/contexts/form-dialog-context'

// TanStack Form types are complex with 11-23 generic parameters, so we use `any` for the form API

export function FormDialogProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [dialogState, setDialogState] = useState<{
    open: boolean
    options: FormDialogOptions<any, any> | null
  }>({
    open: false,
    options: null,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [successData, setSuccessData] = useState<any>(null)

  // Create form instance
  const form = useForm({
    defaultValues: dialogState.options?.formOptions?.defaultValues || {},
    validationLogic: revalidateLogic(),
    onSubmit: async ({ value }) => {
      if (!dialogState.options) return

      setIsLoading(true)
      setError(null)

      try {
        const result = await dialogState.options.onSubmit(value)
        setSuccessData(result)
        setIsLoading(false)

        // Auto-close if showSuccessState is false
        const shouldShowSuccess = dialogState.options.showSuccessState !== false
        if (!shouldShowSuccess) {
          dialogState.options.onSuccess?.(result)
          // Small delay to allow any success side effects
          setTimeout(() => {
            closeFormDialog()
          }, 100)
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('An error occurred'))
        setIsLoading(false)
      }
    },
    onSubmitInvalid: ({ formApi }) => {
      dialogState.options?.formOptions?.onSubmitInvalid?.(formApi as AnyReactFormApi)
    },
  })

  const showFormDialog = useCallback(
    <TFormData, TResponse>(
      options: FormDialogOptions<TFormData, TResponse>,
    ) => {
      setDialogState({
        open: true,
        options: options as FormDialogOptions<any, any>,
      })
      setError(null)
      setSuccessData(null)
      setIsLoading(false)
    },
    [],
  )

  const closeFormDialog = useCallback(() => {
    setDialogState({ open: false, options: null })
    setError(null)
    setSuccessData(null)
    setIsLoading(false)
    form.reset()
    dialogState.options?.onClose?.()
  }, [dialogState.options, form])

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeFormDialog()
    }
  }

  return (
    <FormDialogContext.Provider value={{ showFormDialog, closeFormDialog }}>
      {children}
      <FormDialog
        open={dialogState.open}
        onOpenChange={handleOpenChange}
        options={dialogState.options}
        form={form}
        isLoading={isLoading}
        error={error}
        successData={successData}
      />
    </FormDialogContext.Provider>
  )
}
